import { inject, Injectable } from '@angular/core';
import { map, Observable, of, tap } from 'rxjs';
import { ApiCallingV2Service, API_CONFIG } from '@zayan-erp/shared';
import {
  LotScheduleTaskItem,
  LotScheduleTaskLookupItem,
  ScheduleLotFilters,
  ScheduleWithTasks,
  Stage,
  Task,
  TaskFormItem,
  TaskStatusEnum,
  TaskTableItem
} from '@zayan-erp/scheduler';
import { JobType } from '@zayan-erp/home-building';
import { SetupUser } from '@zayan-erp/shared';

@Injectable({
  providedIn: 'root'
})
export class SchedulerService {
  // #http = inject(ApiCallingService);
  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  baseUrl = this.#apiConfig.baseUrlV2;
  constructor() {}

  readSchedulerUsers(): Observable<SetupUser[]> {
    return this.#http.getData(`${this.baseUrl}/schedule/users`, { showLoader: true, showMessage: false });
  }

  updateSchedulerUser(obj: any, id: string): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/schedule/users/${id}`,
      { showLoader: true, message: 'Warranty user updated successfully', showMessage: true },
      obj
    );
  }

  upsertTask(tasks: TaskTableItem[]): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/schedule/tasks/bulk-upsert`,
      { showLoader: true, showMessage: true, message: 'Tasks added successfully' },
      tasks
    );
  }

  createTask(task: TaskFormItem): Observable<unknown> {
    return this.#http.postData(`${this.baseUrl}/schedule/tasks`, { showLoader: true, showMessage: false }, task);
  }

  readTasks(showActiveResultsOnly: boolean = true): Observable<Task[]> {
    return this.#http.getData(`${this.baseUrl}/schedule/tasks?includingInActive=${showActiveResultsOnly}`, {
      showLoader: true,
      showMessage: false
    });
  }

  updateTask(task: TaskFormItem, id: string): Observable<unknown> {
    return this.#http.putData(`${this.baseUrl}/schedule/tasks/${id}`, task, {
      showLoader: true,
      showMessage: false,
      message: 'Task updated successfully'
    });
  }

  toggleTask(id: string): Observable<unknown> {
    return this.#http.patchData(
      `${this.baseUrl}/schedule/tasks/${id}/toggle-status`,
      {},
      { showLoader: true, showMessage: false }
    );
  }

  readStages(): Observable<Stage[]> {
    return this.#http.getData(`${this.baseUrl}/schedule/stages`, { showLoader: true, showMessage: false });
  }

  readJobTypes(): Observable<JobType[]> {
    return this.#http.getData(`${this.baseUrl}/JobType`, { showLoader: false, showMessage: false });
  }
  readScheduleTaskStatuses(): Observable<TaskStatusEnum[]> {
    return of<TaskStatusEnum[]>(Object.values(TaskStatusEnum));
  }

  readScheduleTasks(params: {
    lotId: string | undefined;
    projectId: string | undefined;
    scheduleId: string | undefined;
    stageId: string | undefined;
    status: string | undefined;
    includeInActive: boolean | undefined;
    searchTerm: string | undefined;
  }): Observable<LotScheduleTaskItem[]> {
    const queryParams = new URLSearchParams();
    if (params.lotId) queryParams.set('lotId', params.lotId);
    if (params.projectId) queryParams.set('projectId', params.projectId);
    if (params.scheduleId) queryParams.set('scheduleId', params.scheduleId);
    if (params.stageId) queryParams.set('stageId', params.stageId);
    if (params.status) queryParams.set('status', params.status);
    if (params.includeInActive) queryParams.set('includingInActive', params.includeInActive.toString());
    if (params.searchTerm) queryParams.set('searchTerm', params.searchTerm);

    return this.#http.getData(`${this.baseUrl}/schedule/schedule-lot?${queryParams.toString()}`, {
      showLoader: true,
      showMessage: false
    });
  }

  createSchedule(schedule: any): Observable<unknown> {
    return this.#http.postData(`${this.baseUrl}/schedule/schedule-tasks`, schedule, {
      showLoader: true,
      showMessage: false,
      message: 'Schedule created successfully'
    });
  }

  readSchedule(): Observable<ScheduleWithTasks[]> {
    return this.#http.getData(`${this.baseUrl}/schedule`, {
      showLoader: true,
      showMessage: false
    });
  }

  readScheduleById(id: string): Observable<ScheduleWithTasks> {
    return this.#http
      .getData(`${this.baseUrl}/schedule/schedule-tasks/${id}/tasks`, {
        showLoader: true,
        showMessage: false
      })
      .pipe(
        map((res: any) => {
          const scheduleData = res;
          let schedule: ScheduleWithTasks = {
            id: scheduleData.scheduleId,
            code: scheduleData.scheduleCode,
            name: scheduleData.scheduleName,
            scheduleTasks: scheduleData.scheduleTasks.map((task: any) => ({
              id: task.id,
              taskId: task.task.id,
              duration: task.duration,
              predecessorId: task.predecessorTaskId ?? null,
              milestone: task.milestone,
              floatDay: task.floatDay ?? 0,
              sequence: task.sequence,
              taskName: task.task.taskName
            }))
          };
          return schedule;
        })
      );
  }

  readScheduleByLotId(lotId: string): Observable<ScheduleWithTasks[]> {
    return of([]);
    // return this.#http.getData('schedule', `lot/${lotId}`, true).pipe(
    //   map((res: any) => {
    //     if (!res.success) throw res;
    //     return res.data;
    //   }),
    // );
  }

  updateSchedule(schedule: any): Observable<unknown> {
    return this.#http.putData(`${this.baseUrl}/schedule/schedule-tasks`, schedule, {
      showLoader: true,
      showMessage: true,
      message: 'Schedule updated successfully'
    });
  }

  updateScheduleLotTask(
    scheduleLotTaskId: string,
    taskData: {
      startDate?: string;
      endDate?: string;
      status?: string;
      exceptionReason?: string;
      startDateIsToday?: boolean;
    }
  ): Observable<unknown> {
    return this.#http.putData(`${this.baseUrl}/schedule/schedule-lot/tasks/${scheduleLotTaskId}`, taskData, {
      showLoader: true,
      showMessage: false
    });
  }

  unSkipTask(scheduleLotTaskId: string): Observable<unknown> {
    return this.#http.putData(
      `${this.baseUrl}/schedule/schedule-lot/tasks/${scheduleLotTaskId}/un-skip`,
      {},
      {
        showLoader: true,
        showMessage: false
      }
    );
  }

  unStartTask(scheduleLotTaskId: string): Observable<unknown> {
    return this.#http.putData(
      `${this.baseUrl}/schedule/schedule-lot/tasks/${scheduleLotTaskId}/un-start`,
      {},
      {
        showLoader: true,
        showMessage: false
      }
    );
  }

  holdAndUHoldTask(scheduleLotTaskId: string): Observable<unknown> {
    return this.#http.patchData(
      `${this.baseUrl}/schedule/schedule-lot/tasks/${scheduleLotTaskId}/on-hold`,
      {},
      {
        showLoader: true,
        showMessage: false
      }
    );
  }

  readOunitLotScheduleData(ounitId: string | null): Observable<any> {
    const queryParams = new URLSearchParams();
    if (ounitId) queryParams.set('unitCodeId', ounitId);

    return this.#http.getData(`${this.baseUrl}/schedule/schedule-lot/by-unit-code?${queryParams.toString()}`, {
      showLoader: true,
      showMessage: false
    });
  }
  readProjectsLotScheduleData(ounitId: string | null, projectId: string | null): Observable<any> {
    const queryParams = new URLSearchParams();
    if (ounitId) queryParams.set('oUnitCodeId', ounitId);
    if (projectId) queryParams.set('projectId', projectId);

    return this.#http.getData(`${this.baseUrl}/schedule/schedule-lot/by-project?${queryParams.toString()}`, {
      showLoader: true,
      showMessage: false
    });
  }
  readLotsLotScheduleData(projectId: string | null, lotId: string | null): Observable<any> {
    const queryParams = new URLSearchParams();
    if (projectId) queryParams.set('projectId', projectId);
    if (lotId) queryParams.set('lotId', lotId);

    return this.#http.getData(`${this.baseUrl}/schedule/schedule-lot/by-lot?${queryParams.toString()}`, {
      showLoader: true,
      showMessage: false
    });
  }

  publishSchedule(scheduleData: any) {
    return this.#http.postData(
      `${this.baseUrl}/schedule/schedule-lot`,
      {
        showLoader: true,
        showMessage: false
      },
      scheduleData
    );
  }
  readScheduleLots(
    filters: ScheduleLotFilters
  ): Observable<{ totalCount: number; scheduleLotTaskListDtos: LotScheduleTaskLookupItem[] }> {
    const queryParams = this.buildQueryParams(filters);
    return this.#http.getData(`${this.baseUrl}/schedule/schedule-lot/filtered-tasks?${queryParams.toString()}`, {
      showLoader: true,
      showMessage: false
    });
  }
  private buildQueryParams(params: Record<string, any>): string {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.set(key, value.toString());
      }
    });

    return queryParams.toString();
  }

  bulkTaskCompletion(lotTaskIds: any): Observable<unknown> {
    return this.#http.putData(`${this.baseUrl}/schedule/schedule-lot/tasks/bulk-completion`, lotTaskIds, {
      showLoader: true,
      showMessage: false
    });
  }

  readVpoByScheduleLotTaskId(scheduleLotTaskId: string): Observable<any> {
    return this.#http.getData(`${this.baseUrl}/schedule/schedule-lot/tasks/${scheduleLotTaskId}/vpo`, {
      showLoader: true,
      showMessage: false
    });
  }

  createVpo(vpo: any): Observable<any> {
    return this.#http.postData(`${this.baseUrl}/schedule/schedule-lot/tasks/vpo`, vpo, {
      showLoader: true,
      showMessage: false
    });
  }

  updateVpo(vpo: any): Observable<any> {
    return this.#http.putData(`${this.baseUrl}/schedule/schedule-lot/tasks/vpo`, vpo, {
      showLoader: true,
      showMessage: false
    });
  }
}
